import {
  type AccessorColumnDef,
  type VisibilityState,
} from '@tanstack/react-table';
import { useMemo } from 'react';

/**
 * Generate memoised tanstack `VisibilityState` and `ColumnOrderState` from the `columnVisibilityAndOrder`
 * state controlled by the `ColumnVisibilityMenu` component.
 *
 * @param columnDefs - the column definitions used in the table.
 * @param columnVisibilityAndOrder - the array of column IDs that are visible and in order.
 */
export const useColumnVisibilityAndOrderState = <
  TData = unknown,
  ColumnIds extends string = string,
>(
  columnDefs: AccessorColumnDef<TData, any>[],
  columnVisibilityAndOrder: ColumnIds[],
) =>
  useMemo(
    () => ({
      columnOrder: columnVisibilityAndOrder,
      columnVisibility: Object.fromEntries([
        ...columnDefs.map((columnDef) =>
          /**
           * I can't seem to type `columnDefs` as `ColumnDef<TData, TValue>[]` from `tanstack/react-table`
           * and have TS be happy with it. It will always raise a cryptic error when we pass the actual column
           * definitions on usage. Something to do with the `accessorFn` return types not matching?
           */
          'id' in columnDef ? [columnDef.id, false] : ['', false],
        ),
        ...columnVisibilityAndOrder.map((columnId) => [columnId, true]),
      ]) as VisibilityState,
    }),
    [columnVisibilityAndOrder],
  );
